import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FolderKanban, Lock, Globe2, Users, MoreHorizontal, Pencil, Settings2,
  Pin, PinOff, Archive, Trash2, ArchiveRestore, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useWorkspaceProjects, renameProject, pinProject, archiveProject,
  deleteProject, reorderProjectPins, moveConversation, useArchived,
  CONVERSATION_DRAG_TYPE, PROJECT_DRAG_TYPE,
} from "@/lib/workspaceProjects";
import { EditableName } from "@/components/workspace/RailEditableName";

export const VISIBILITY_ICON = { private: Lock, organization: Globe2, members: Users, project: Globe2 };
const iconBtn = "w-6 h-6 rounded-md flex items-center justify-center text-[var(--qn-foreground-subtle)] opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-[var(--qn-surface-hover)]";

export default function RailProjects({ collapsed }) {
  const navigate = useNavigate();
  const { sub } = useParams();
  const { projects } = useWorkspaceProjects();
  const [editingId, setEditingId] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const { projects: archivedProjects } = useArchived(showArchived);
  if (collapsed) return null;
  const canonical = projects.filter((p) => p.project_id).sort((a, b) => {
    const ap = a.pinned ? 0 : 1, bp = b.pinned ? 0 : 1;
    if (ap !== bp) return ap - bp;
    if (a.pinned && b.pinned) return (a.pin_order ?? 9e15) - (b.pin_order ?? 9e15);
    return 0;
  });
  const pinnedIds = canonical.filter((p) => p.pinned).map((p) => p.project_id);

  const reorderProjects = async (draggedId, targetId) => {
    if (!pinnedIds.includes(draggedId) || draggedId === targetId) return;
    const ids = [...pinnedIds];
    ids.splice(ids.indexOf(draggedId), 1);
    const to = ids.indexOf(targetId);
    ids.splice(to < 0 ? ids.length : to, 0, draggedId);
    try { await reorderProjectPins(ids); } catch { toast.error("Could not reorder pins"); }
  };
  const rowDnd = (p) => ({
    draggable: p.pinned,
    onDragStart: p.pinned ? (e) => { e.dataTransfer.setData(PROJECT_DRAG_TYPE, p.project_id); e.dataTransfer.effectAllowed = "move"; } : undefined,
    onDragOver: (e) => {
      const t = [...e.dataTransfer.types];
      if (t.includes(CONVERSATION_DRAG_TYPE) || (p.pinned && t.includes(PROJECT_DRAG_TYPE))) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }
    },
    onDrop: async (e) => {
      const conv = e.dataTransfer.getData(CONVERSATION_DRAG_TYPE);
      const proj = e.dataTransfer.getData(PROJECT_DRAG_TYPE);
      if (conv) { e.preventDefault(); try { await moveConversation(conv, p.project_id); toast.success(`Chat moved to “${p.name}”`); } catch { toast.error("Could not move the chat"); } return; }
      if (proj && p.pinned) { e.preventDefault(); reorderProjects(proj, p.project_id); }
    },
  });

  const commitRename = async (p, name) => {
    setEditingId(null);
    try { await renameProject(p.project_id, name); toast.success("Project renamed"); }
    catch { toast.error("Could not rename the project"); }
  };
  const togglePin = async (p) => {
    try { await pinProject(p.project_id, !p.pinned); toast.success(p.pinned ? "Unpinned" : "Pinned"); }
    catch { toast.error("Could not update the project"); }
  };
  const setArchived = async (p, val) => {
    try { await archiveProject(p.project_id, val); toast.success(val ? "Project archived" : "Project unarchived"); }
    catch { toast.error("Could not update the project"); }
  };
  const confirmDelete = async () => {
    const p = pendingDelete;
    setPendingDelete(null);
    if (!p) return;
    try { await deleteProject(p.project_id); toast.success("Project deleted"); }
    catch { toast.error("Could not delete the project"); }
  };

  return (
    <div className="pl-6 pr-1 pb-1 space-y-0.5" data-testid="rail-projects">
      {canonical.length === 0 && (
        <div className="px-3 py-1 text-[11px] text-[var(--qn-foreground-subtle)]" data-testid="rail-projects-empty">No projects yet</div>
      )}
      {canonical.map((p) => {
        const Vis = VISIBILITY_ICON[p.visibility] || Globe2;
        const active = sub === p.project_id;
        const editing = editingId === p.project_id;
        return (
          <div key={p.project_id} className="group flex items-center gap-1" data-testid={`rail-project-row-${p.project_id}`}>
            {editing ? (
              <EditableName value={p.name} onCommit={(n) => commitRename(p, n)} onCancel={() => setEditingId(null)} />
            ) : (
              <button data-testid={`rail-project-${p.project_id}`}
                onClick={() => navigate(`/app/projects/${p.project_id}`)}
                onDoubleClick={() => { if (p.is_owner) setEditingId(p.project_id); }}
                {...rowDnd(p)}
                className={`flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 rounded-md text-xs truncate transition-colors ${
                  active ? "text-[var(--qn-primary)] bg-[var(--qn-primary-muted)]" : "text-[var(--qn-foreground-muted)] hover:text-[var(--qn-foreground)] hover:bg-[var(--qn-surface-hover)]"} ${p.pinned ? "cursor-grab active:cursor-grabbing" : ""}`}>
                {p.pinned ? <Pin className="w-3 h-3 shrink-0 text-[var(--qn-primary)]" /> : <FolderKanban className="w-3.5 h-3.5 shrink-0" />}
                <span className="truncate">{p.name}</span>
                <Vis className="w-3 h-3 ml-auto shrink-0 opacity-60" aria-label={p.visibility} />
              </button>
            )}
            {!editing && p.is_owner && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid={`rail-project-menu-${p.project_id}`} aria-label="Project options" className={iconBtn}>
                    <MoreHorizontal className="w-3.5 h-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  <DropdownMenuItem data-testid={`rail-project-rename-${p.project_id}`} onClick={() => setEditingId(p.project_id)} className="text-xs">
                    <Pencil className="w-3.5 h-3.5 mr-2" /> Rename
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`rail-project-editdetails-${p.project_id}`} onClick={() => navigate(`/app/projects/${p.project_id}`)} className="text-xs">
                    <Settings2 className="w-3.5 h-3.5 mr-2" /> Edit details
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`rail-project-pin-${p.project_id}`} onClick={() => togglePin(p)} className="text-xs">
                    {p.pinned ? <PinOff className="w-3.5 h-3.5 mr-2" /> : <Pin className="w-3.5 h-3.5 mr-2" />} {p.pinned ? "Unpin" : "Pin"}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem data-testid={`rail-project-archive-${p.project_id}`} onClick={() => setArchived(p, true)} className="text-xs">
                    <Archive className="w-3.5 h-3.5 mr-2" /> Archive
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid={`rail-project-delete-${p.project_id}`} onClick={() => setPendingDelete(p)} className="text-xs text-red-500 focus:text-red-500">
                    <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        );
      })}

      <button data-testid="rail-projects-archived-toggle" onClick={() => setShowArchived((v) => !v)}
        className="w-full flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[10px] uppercase tracking-[0.14em] font-semibold text-[var(--qn-foreground-subtle)] hover:bg-[var(--qn-surface-hover)]">
        <ChevronRight className={`w-3 h-3 transition-transform ${showArchived ? "rotate-90" : ""}`} /> <Archive className="w-3 h-3" /> Archived
      </button>
      {showArchived && (
        <div data-testid="rail-projects-archived-section" className="space-y-0.5">
          {archivedProjects.length === 0 && <div className="px-3 pb-1 text-[11px] text-[var(--qn-foreground-subtle)]">No archived projects</div>}
          {archivedProjects.map((p) => (
            <div key={p.project_id} className="group flex items-center gap-1" data-testid={`rail-archived-project-${p.project_id}`}>
              <span className="flex-1 min-w-0 px-3 py-1.5 text-xs truncate text-[var(--qn-foreground-subtle)] flex items-center gap-2">
                <FolderKanban className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{p.name}</span>
              </span>
              {p.is_owner && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`rail-archived-project-menu-${p.project_id}`} aria-label="Archived project options" className={iconBtn}>
                      <MoreHorizontal className="w-3.5 h-3.5" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    <DropdownMenuItem data-testid={`rail-archived-project-unarchive-${p.project_id}`} onClick={() => setArchived(p, false)} className="text-xs">
                      <ArchiveRestore className="w-3.5 h-3.5 mr-2" /> Unarchive
                    </DropdownMenuItem>
                    <DropdownMenuItem data-testid={`rail-archived-project-delete-${p.project_id}`} onClick={() => setPendingDelete(p)} className="text-xs text-red-500 focus:text-red-500">
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => { if (!o) setPendingDelete(null); }}>
        <AlertDialogContent data-testid="rail-project-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              “{pendingDelete?.name}” will be removed from your workspace. Its chats are kept. This can be recovered by an administrator.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rail-project-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="rail-project-delete-confirm" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
